import { z } from 'zod';
import type { LegReport } from './good-weather.js';
import { FUEL_TYPES } from './lists.js';
import type { ParseResult, SchemaIssue } from './schemas.js';

/**
 * Charter party kaydı, dönemi ve dönem dışı seyirler (ADR 0084; ortağın `sefer-4.jpeg`,
 * `sefer-5.jpeg`). CP şirket verisidir: ofis yazar, o gemiye iner, gemide salt okunur. Dönem
 * gemideki On Hire / Off Hire raporlarından türer; sefer başlangıcı CP başlangıcı sayılmaz (S15).
 * Saf fonksiyonlar; zaman parametreyle gelir.
 */

export const CP_TYPES = ['time', 'voyage'] as const;
export type CpType = (typeof CP_TYPES)[number];

/** `office` ofiste girildi; `migrated` seferin CP alanlarından dönüştürüldü (ADR 0084 §5). */
export const CP_SOURCES = ['office', 'migrated'] as const;
export type CpSource = (typeof CP_SOURCES)[number];

/** Sözleşmenin hariç tuttuğu durumlar (kayıt; hesap ADR 0022 §4 kuralıyla aynı kalır). */
export const CP_EXCLUSIONS = [
  'canal',
  'strait',
  'anchorage',
  'drift',
  'waiting',
  'port',
  'manual',
] as const;
export type CpExclusion = (typeof CP_EXCLUSIONS)[number];

/** Dönem dışı seyrin sınıfı (ortağın 2026-10-13 notu; ADR 0084 §4). */
export const PASSAGE_CLASSES = ['owners_account', 'awaiting_employment', 'unclassified'] as const;
export type PassageClass = (typeof PASSAGE_CLASSES)[number];

/** Olay izi (ADR 0084 §6): hariç/dahil kararı, inceleme ve dönem dışı sınıflama. */
export const CP_EVENT_ACTIONS = ['exclude', 'include', 'review', 'classify'] as const;
export type CpEventAction = (typeof CP_EVENT_ACTIONS)[number];
/** `day` Noon raporu, `leg` seyir penceresi (COSP), `passage` dönem dışı pencere (COSP). */
export const CP_EVENT_TARGETS = ['day', 'leg', 'passage'] as const;
export type CpEventTarget = (typeof CP_EVENT_TARGETS)[number];

/** Gerekçe, not ve dış kaynak en çok 1000 karakter. */
export const CP_TEXT_MAX = 1000;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const num = (min: number, max: number) =>
  z.number(typed('INVALID_NUMBER')).min(min, 'OUT_OF_RANGE').max(max, 'OUT_OF_RANGE');
const optNum = (min: number, max: number) => num(min, max).nullable().default(null);
const optInt = (min: number, max: number) =>
  num(min, max).int('NOT_INTEGER').nullable().default(null);
const optText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .trim()
    .max(max, 'TOO_LONG')
    .nullish()
    .transform((v) => (v ? v : null));
const datetime = () => z.iso.datetime(typed('INVALID_DATETIME'));
const uuid = () => z.uuid('INVALID');

/** CP şartları (`charter_parties.terms`, JSON; ADR 0084 §1). */
export const charterPartyTermsSchema = z.object({
  deliveryPlace: optText(200),
  redeliveryPlace: optText(200),
  speedLadenKn: optNum(0, 40),
  speedBallastKn: optNum(0, 40),
  consumptionLadenMt: optNum(0, 500),
  consumptionBallastMt: optNum(0, 500),
  mainFuel: z.enum(FUEL_TYPES, typed('INVALID_OPTION')).nullable().default(null),
  auxFuel: z.enum(FUEL_TYPES, typed('INVALID_OPTION')).nullable().default(null),
  /** "About" toleransları; varsayılan yok (en sıkı; ADR 0024 #16). */
  speedToleranceKn: optNum(0, 5),
  consumptionTolerancePct: optNum(0, 25),
  /** İyi hava sınırları (ADR 0024 #17): Beaufort, Douglas deniz durumu, Hs, swell. */
  maxBeaufort: optInt(0, 12),
  maxDouglas: optInt(0, 9),
  maxWaveHeightM: optNum(0, 30),
  maxSwellM: optNum(0, 30),
  currentClause: optText(300),
  /** Hesaba girecek en kısa gün (saat); boşsa her gün. */
  minDayHours: optNum(0, 24),
  exclusions: z
    .array(z.enum(CP_EXCLUSIONS, typed('INVALID_OPTION')))
    .default([])
    .refine((list) => new Set(list).size === list.length, 'DUPLICATE'),
  notes: optText(2000),
});
export type CharterPartyTerms = z.output<typeof charterPartyTermsSchema>;

export const charterPartySchema = z
  .object({
    charterer: optText(200),
    cpNo: optText(60),
    cpDate: z
      .string(typed('INVALID_DATE'))
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'INVALID_DATE')
      .nullable()
      .default(null),
    cpType: z.enum(CP_TYPES, typed('INVALID_OPTION')).default('time'),
    deliveryReportId: uuid().nullable().default(null),
    redeliveryReportId: uuid().nullable().default(null),
    deliveryAt: datetime().nullable().default(null),
    redeliveryAt: datetime().nullable().default(null),
    terms: charterPartyTermsSchema.default(() => charterPartyTermsSchema.parse({})),
  })
  .superRefine((value, ctx) => {
    if (
      value.deliveryAt !== null &&
      value.redeliveryAt !== null &&
      Date.parse(value.redeliveryAt) < Date.parse(value.deliveryAt)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['redeliveryAt'],
        message: 'REDELIVERY_BEFORE_DELIVERY',
      });
    }
    if (value.deliveryReportId !== null && value.deliveryReportId === value.redeliveryReportId) {
      ctx.addIssue({ code: 'custom', path: ['redeliveryReportId'], message: 'SAME_REPORT' });
    }
  });
export type CharterPartyContent = z.output<typeof charterPartySchema>;

const issuesOf = (error: z.ZodError): SchemaIssue[] =>
  error.issues.map((i) => ({ path: i.path.map(String).join('.'), code: i.message }));

/**
 * CP kaydının içeriği. Ofis kaydında kiracı ve CP no zorunludur; dönüştürülen kayıtta seferde ne
 * varsa odur (ADR 0084 §1, §5).
 */
export function parseCharterParty(
  input: unknown,
  options: { source: CpSource },
): ParseResult<CharterPartyContent> {
  const result = charterPartySchema.safeParse(input);
  const issues = result.success ? [] : issuesOf(result.error);
  if (options.source === 'office' && result.success) {
    if (result.data.charterer === null) issues.push({ path: 'charterer', code: 'REQUIRED' });
    if (result.data.cpNo === null) issues.push({ path: 'cpNo', code: 'REQUIRED' });
  } else if (options.source === 'office' && input && typeof input === 'object') {
    const raw = input as Record<string, unknown>;
    for (const key of ['charterer', 'cpNo'] as const) {
      if (typeof raw[key] !== 'string' || (raw[key] as string).trim() === '') {
        if (!issues.some((i) => i.path === key)) issues.push({ path: key, code: 'REQUIRED' });
      }
    }
  }
  if (!result.success || issues.length > 0) return { ok: false, issues };
  return { ok: true, value: result.data };
}

/* ---------- Veri dönüşümü (ADR 0084 §5) ---------- */

export interface VoyageCpColumns {
  charterer: string | null;
  charterPartyNo: string | null;
  cpSpeedLadenKn: number | null;
  cpSpeedBallastKn: number | null;
  cpConsumptionLadenMt: number | null;
  cpConsumptionBallastMt: number | null;
  mainEngineFuel: string | null;
  auxEngineFuel: string | null;
  cpMaxBeaufort: number | null;
  cpMaxWaveHeightM: number | null;
}

const filled = (value: unknown) =>
  value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '');
const fuel = (value: string | null) =>
  value !== null && (FUEL_TYPES as readonly string[]).includes(value)
    ? (value as (typeof FUEL_TYPES)[number])
    : null;

/**
 * Seferin CP kolonlarından CP kaydının içeriği; CP alanı boşsa `null`. Yakıt seferin özelliğidir,
 * tek başına CP alanı sayılmaz. Dönem seferden türer (`cpPeriod`, dayanak `voyage`).
 */
export function charterPartyFromVoyage(voyage: VoyageCpColumns): CharterPartyContent | null {
  const cpFields = [
    voyage.charterer,
    voyage.charterPartyNo,
    voyage.cpSpeedLadenKn,
    voyage.cpSpeedBallastKn,
    voyage.cpConsumptionLadenMt,
    voyage.cpConsumptionBallastMt,
    voyage.cpMaxBeaufort,
    voyage.cpMaxWaveHeightM,
  ];
  if (!cpFields.some(filled)) return null;
  const text = (value: string | null) => (filled(value) ? String(value).trim() : null);
  return charterPartySchema.parse({
    charterer: text(voyage.charterer),
    cpNo: text(voyage.charterPartyNo),
    cpDate: null,
    cpType: 'time',
    deliveryReportId: null,
    redeliveryReportId: null,
    deliveryAt: null,
    redeliveryAt: null,
    terms: {
      speedLadenKn: voyage.cpSpeedLadenKn,
      speedBallastKn: voyage.cpSpeedBallastKn,
      consumptionLadenMt: voyage.cpConsumptionLadenMt,
      consumptionBallastMt: voyage.cpConsumptionBallastMt,
      mainFuel: fuel(voyage.mainEngineFuel),
      auxFuel: fuel(voyage.auxEngineFuel),
      maxBeaufort: voyage.cpMaxBeaufort,
      maxWaveHeightM: voyage.cpMaxWaveHeightM,
    },
  });
}

/* ---------- Dönem (ADR 0084 §2) ---------- */

export interface CharterPartyPeriodSource {
  id: string;
  source: CpSource;
  /** Dönüştürülen kaydın kaynak seferi. */
  voyageId: string | null;
  deliveryReportId: string | null;
  redeliveryReportId: string | null;
  deliveryAt: string | null;
  redeliveryAt: string | null;
}

export type CpPeriodBasis = 'report' | 'voyage' | 'planned';
export type CpStatus = 'planned' | 'on_hire' | 'redelivered';

export interface CpPeriod {
  start: string | null;
  end: string | null;
  startBasis: CpPeriodBasis | null;
  endBasis: CpPeriodBasis | null;
  status: CpStatus;
}

const iso = (value: string) => new Date(value).toISOString();

/**
 * CP dönemi: başlangıç bağlı On Hire raporu (gönderilmiş) → dönüştürülen kayıtta kaynak seferin
 * başlangıcı → planlanan delivery; bitiş Off Hire → seferin bitişi → planlanan redelivery. Taslak
 * ya da iptal edilmiş rapor sayılmaz. Planlanan tarih olgu değildir: durum ancak gerçek bir
 * başlangıçla `on_hire` olur.
 */
export function cpPeriod(
  cp: CharterPartyPeriodSource,
  ctx: {
    reports: readonly Pick<LegReport, 'id' | 'kind' | 'reportedAt' | 'status'>[];
    voyage: { start: string | null; end: string | null } | null;
  },
): CpPeriod {
  const reportAt = (id: string | null, kind: 'on_hire' | 'off_hire') => {
    if (id === null) return null;
    const found = ctx.reports.find((r) => r.id === id);
    return found && found.kind === kind && found.status === 'submitted'
      ? iso(found.reportedAt)
      : null;
  };
  const fromVoyage = cp.source === 'migrated' && cp.voyageId !== null ? ctx.voyage : null;
  const pick = (
    report: string | null,
    voyage: string | null | undefined,
    planned: string | null,
  ): [string | null, CpPeriodBasis | null] => {
    if (report) return [report, 'report'];
    if (voyage) return [iso(voyage), 'voyage'];
    if (planned) return [iso(planned), 'planned'];
    return [null, null];
  };
  const [start, startBasis] = pick(
    reportAt(cp.deliveryReportId, 'on_hire'),
    fromVoyage?.start,
    cp.deliveryAt,
  );
  const [end, endBasis] = pick(
    reportAt(cp.redeliveryReportId, 'off_hire'),
    fromVoyage?.start ? fromVoyage.end : null,
    cp.redeliveryAt,
  );
  const actualStart = startBasis === 'report' || startBasis === 'voyage';
  const actualEnd = endBasis === 'report' || endBasis === 'voyage';
  return {
    start,
    end,
    startBasis,
    endBasis,
    status: !actualStart ? 'planned' : actualEnd ? 'redelivered' : 'on_hire',
  };
}

const ms = (value: string | null, open: number) => (value === null ? open : Date.parse(value));

/**
 * Örtüşen CP dönemleri (ADR 0084 §1): her kayıt için örtüştüğü kayıtlar. Uç uca değmek serbest;
 * başlangıcı olmayan dönem hiçbiriyle örtüşmez. Reddedilmez, register'da işaretlenir.
 */
export function periodOverlaps(
  periods: readonly { id: string; start: string | null; end: string | null }[],
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const live = periods.filter((p) => p.start !== null);
  for (const a of live) {
    for (const b of live) {
      if (a.id === b.id) continue;
      const overlap =
        ms(a.start, 0) < ms(b.end, Number.POSITIVE_INFINITY) &&
        ms(b.start, 0) < ms(a.end, Number.POSITIVE_INFINITY);
      if (overlap) out.set(a.id, [...(out.get(a.id) ?? []), b.id]);
    }
  }
  return out;
}

/* ---------- Seyir pencereleri ve kesişim (ADR 0084 §3) ---------- */

export interface SailingWindow {
  /** COSP raporunun kimliği. */
  id: string;
  start: string;
  /** EOSP; yoksa (seyir sürüyor ya da araya COSP girdi) `null`. */
  end: string | null;
  endId: string | null;
  /** Penceredeki son raporun zamanı (açık pencerenin kesişimi için). */
  lastAt: string;
  /** EOSP yok ve sonra COSP gelmedi: seyir sürüyor. */
  ongoing: boolean;
}

const byTime = (a: { reportedAt: string }, b: { reportedAt: string }) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/**
 * Geminin seyir pencereleri: gönderilmiş raporlarda her COSP → sonraki EOSP; araya COSP girerse
 * pencere orada biter (EOSP'suz). `voyagePerformance`'taki CP penceresiyle aynı kural; sefere
 * bakılmaz (ADR 0083 §1).
 */
export function sailingWindows(
  reports: readonly Pick<LegReport, 'id' | 'kind' | 'reportedAt' | 'status'>[],
): SailingWindow[] {
  const sorted = reports.filter((r) => r.status === 'submitted').sort(byTime);
  const out: SailingWindow[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const cosp = sorted[i];
    if (cosp?.kind !== 'cosp') continue;
    let j = i + 1;
    while (j < sorted.length && sorted[j]?.kind !== 'eosp' && sorted[j]?.kind !== 'cosp') j++;
    const eosp = sorted[j]?.kind === 'eosp' ? sorted[j] : undefined;
    const last = eosp ? j : Math.min(j, sorted.length) - 1;
    out.push({
      id: cosp.id,
      start: iso(cosp.reportedAt),
      end: eosp ? iso(eosp.reportedAt) : null,
      endId: eosp?.id ?? null,
      lastAt: iso((sorted[last] ?? cosp).reportedAt),
      ongoing: !eosp && j >= sorted.length,
    });
    i = eosp ? j : last;
  }
  return out;
}

export type WindowRelation = 'inside' | 'partial' | 'outside';

/**
 * Pencerenin CP dönemiyle ilişkisi. Açık pencere sonraki COSP'tan önce bittiyse son raporunda
 * biter; seyir sürüyorsa açıktır. Dönemin başlangıcı yoksa (tarihsiz kayıt) her pencere dışarıda.
 */
export function windowRelation(
  window: SailingWindow,
  period: { start: string | null; end: string | null },
): WindowRelation {
  if (period.start === null) return 'outside';
  const open = Number.POSITIVE_INFINITY;
  const wStart = Date.parse(window.start);
  const wEnd =
    window.end !== null
      ? Date.parse(window.end)
      : window.ongoing
        ? open
        : Date.parse(window.lastAt);
  const pStart = Date.parse(period.start);
  const pEnd = ms(period.end, open);
  if (wStart >= pStart && wEnd <= pEnd) return 'inside';
  if (wStart < pEnd && wEnd > pStart) return 'partial';
  return 'outside';
}

/* ---------- Dönem dışı seyirler (ADR 0084 §4) ---------- */

const EMPLOYMENT_CLASS: Record<string, PassageClass> = {
  owners_operation: 'owners_account',
  maintenance: 'owners_account',
  unemployed: 'awaiting_employment',
};

/**
 * Önerilen sınıf: pencereden önceki son Departure'ın `classification.employment`'ı. Kiralı
 * denmiş ama CP kaydı yoksa ya da bilgi yoksa Unclassified (kiracıya yazılmaz).
 */
export function suggestedPassageClass(
  reports: readonly Pick<LegReport, 'id' | 'kind' | 'reportedAt' | 'status' | 'data'>[],
  windowId: string,
): PassageClass {
  const cosp = reports.find((r) => r.id === windowId);
  if (!cosp) return 'unclassified';
  const departure = reports
    .filter(
      (r) =>
        r.kind === 'departure' &&
        r.status === 'submitted' &&
        Date.parse(r.reportedAt) <= Date.parse(cosp.reportedAt),
    )
    .sort(byTime)
    .at(-1);
  const employment = (departure?.data.classification as { employment?: unknown } | undefined)
    ?.employment;
  return (typeof employment === 'string' && EMPLOYMENT_CLASS[employment]) || 'unclassified';
}

export interface CpEvent {
  id: string;
  charterPartyId: string | null;
  action: CpEventAction;
  targetKind: CpEventTarget | null;
  targetId: string | null;
  classification: PassageClass | null;
  reason: string | null;
  reference: string | null;
  contentHash: string | null;
  createdAt: string;
  createdBy: string;
  createdByName: string | null;
}

const eventOrder = (a: CpEvent, b: CpEvent) =>
  Date.parse(a.createdAt) - Date.parse(b.createdAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Hedef başına son karar (`<tür>:<kimlik>`): dahil etmek hariç tutmayı geri alır; iz kalır.
 * `charterPartyId` `null` ise dönem dışı sınıflamalar.
 */
export function latestCpDecisions(
  events: readonly CpEvent[],
  charterPartyId: string | null,
): Map<string, CpEvent> {
  const out = new Map<string, CpEvent>();
  for (const event of [...events].sort(eventOrder)) {
    if (event.charterPartyId !== charterPartyId) continue;
    if (event.action === 'review' || event.targetKind === null || event.targetId === null) continue;
    out.set(`${event.targetKind}:${event.targetId}`, event);
  }
  return out;
}

export interface PassageClassRow extends SailingWindow {
  suggested: PassageClass;
  classification: PassageClass;
  decided: boolean;
  decision: CpEvent | null;
}

/**
 * Hiçbir CP dönemine (kısmen de) girmeyen seyir pencereleri ve sınıfları. Öneri Departure'dan;
 * ofisin son kararı öneriyi geçer; varsayılan Unclassified.
 */
export function passageClasses(
  reports: readonly LegReport[],
  periods: readonly { start: string | null; end: string | null }[],
  events: readonly CpEvent[],
): PassageClassRow[] {
  const decisions = latestCpDecisions(
    events.filter((e) => e.action === 'classify'),
    null,
  );
  return sailingWindows(reports)
    .filter((window) => periods.every((period) => windowRelation(window, period) === 'outside'))
    .map((window) => {
      const decision = decisions.get(`passage:${window.id}`) ?? null;
      const suggested = suggestedPassageClass(reports, window.id);
      return {
        ...window,
        suggested,
        classification: decision?.classification ?? 'unclassified',
        decided: decision !== null,
        decision,
      };
    });
}

/* ---------- Olay doğrulaması (ADR 0084 §6) ---------- */

const reasonText = z.string().trim().max(CP_TEXT_MAX, 'TOO_LONG').nullish();

export const cpEventSchema = z
  .object({
    charterPartyId: uuid().nullable(),
    action: z.enum(CP_EVENT_ACTIONS, typed('INVALID_OPTION')),
    targetKind: z.enum(CP_EVENT_TARGETS, typed('INVALID_OPTION')).nullable(),
    targetId: uuid().nullable(),
    classification: z.enum(PASSAGE_CLASSES, typed('INVALID_OPTION')).nullable(),
    reason: reasonText,
    reference: reasonText,
    contentHash: z
      .string()
      .regex(/^[0-9a-f]{64}$/, 'INVALID')
      .nullable(),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    const reason = value.reason?.trim() ?? '';
    if (value.action === 'review') {
      if (value.charterPartyId === null) issue('charterPartyId', 'REQUIRED');
      if (value.contentHash === null) issue('contentHash', 'REQUIRED');
      if (value.targetKind !== null) issue('targetKind', 'NOT_ALLOWED');
      return;
    }
    if (reason === '') issue('reason', 'REQUIRED');
    if (value.targetId === null) issue('targetId', 'REQUIRED');
    if (value.action === 'classify') {
      if (value.targetKind !== 'passage') issue('targetKind', 'INVALID_OPTION');
      if (value.classification === null) issue('classification', 'REQUIRED');
      if (value.charterPartyId !== null) issue('charterPartyId', 'NOT_ALLOWED');
      return;
    }
    if (value.targetKind !== 'day' && value.targetKind !== 'leg') {
      issue('targetKind', 'INVALID_OPTION');
    }
    if (value.charterPartyId === null) issue('charterPartyId', 'REQUIRED');
    if (value.classification !== null) issue('classification', 'NOT_ALLOWED');
  });
export type CpEventContent = z.output<typeof cpEventSchema>;

export function parseCpEvent(input: unknown): ParseResult<CpEventContent> {
  const result = cpEventSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}
